import React, { InputHTMLAttributes } from 'react';
import classNames from 'classnames';
import styled from '@emotion/styled';
import { useFocusRing } from '@react-aria/focus';
import { semantic_colors, colors } from '../../foundation';
import { absolute_center, buildCSSWithLength, hidden } from '../../styles';
import { default_radio_size_css, getRadioStyleByProps } from './styles';
import { AriaFocusProps } from '../../types/common';
import { RadioCategoryType, RadioItemType, RadioWidthType } from './RadioGroup';
import { DefaultRadioSizeUnion } from './types';

interface RadioBaseType
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size' | 'width'>,
    AriaFocusProps {}

export interface RadioProps extends RadioBaseType, RadioCategoryType<RadioItemType> {
  /** @default false */
  fill?: boolean;
  /** @default 'auto' */
  width?: RadioWidthType;
  className?: string;
}

export const Radio = React.forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      fill = false,
      width = 'auto',
      className,
      size = 'medium',
      children,
      disabled,
      value,
      radioItemType = 'default',
      unstableFocusVisible,
      ...props
    },
    ref,
  ) => {
    const { isFocusVisible, focusProps } = useFocusRing({ within: true });
    return (
      <label
        className={classNames('pds-radio', className, { checked: props.checked })}
        css={[
          getRadioStyleByProps({
            radioItemType,
            fill,
            width,
            size,
            disabled,
            checked: props.checked,
            focused: unstableFocusVisible ?? isFocusVisible,
          }),
        ]}
        {...focusProps}
      >
        <HiddenInput type='radio' value={value} ref={ref} disabled={disabled} {...props} />
        {radioItemType === 'default' ? (
          <>
            <FakeRadio disabled={disabled} data-role='radio-mark' size={size as DefaultRadioSizeUnion} />
            {children && (
              <Text disabled={disabled} size={size as DefaultRadioSizeUnion}>
                {children}
              </Text>
            )}
          </>
        ) : (
          children
        )}
      </label>
    );
  },
);

const HiddenInput = styled.input<{ disabled?: boolean }>`
  ${hidden};

  &:focus {
    outline: none;
    box-shadow: none;
  }
  &:checked + [data-role='radio-mark']:before {
    background-color: ${colors.white};
    border: ${({ disabled }) =>
      disabled ? `1px solid ${semantic_colors.border.disabled}` : `1px solid ${semantic_colors.accent.primary}`};
  }
  &:checked + [data-role='radio-mark']:after {
    opacity: 1;
  }
`;

const FakeRadio = styled.span<{ disabled?: boolean; size: DefaultRadioSizeUnion }>`
  display: block;
  position: relative;
  transition: all 0.1s linear;
  ${({ size }) => `
    ${buildCSSWithLength('width', default_radio_size_css[size].base)}
    ${buildCSSWithLength('height', default_radio_size_css[size].base)}
    margin: ${default_radio_size_css[size].markMargin};
  `};

  &:hover::before {
    border: 1px solid ${({ disabled }) => (disabled ? semantic_colors.border.primary : semantic_colors.accent.primary)};
  }
  &:before,
  &:after {
    transition: all 0.1s linear;
  }
  &:before {
    ${absolute_center};
    content: '';
    width: 100%;
    height: 100%;
    border: 1px solid
      ${({ disabled }) => (disabled ? semantic_colors.border.primary : semantic_colors.border.secondary)};
    border-radius: 50%;
    background-color: ${({ disabled }) =>
      disabled ? semantic_colors.fill.disabled : semantic_colors.background.surface};
  }
  &:after {
    ${absolute_center};
    content: '';
    opacity: 0;
    ${({ size }) => `
      ${buildCSSWithLength('width', default_radio_size_css[size].fake)};
      ${buildCSSWithLength('height', default_radio_size_css[size].fake)};
    `};
    border-radius: 50%;
    background-color: ${({ disabled }) =>
      disabled ? semantic_colors.accent.disabled : semantic_colors.accent.primary};
  }
`;

const Text = styled.span<{ disabled?: boolean; size: DefaultRadioSizeUnion }>`
  color: ${({ disabled }) => (disabled ? semantic_colors.content.disabled : semantic_colors.content.primary)};
  ${({ size }) => `${buildCSSWithLength('margin-left', default_radio_size_css[size].spacing)};
    ${default_radio_size_css[size].font};
  `};
`;
